import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import { toast } from 'react-toastify';
import PublicHeader from '../components/PublicHeader';
import Footer from '../components/Footer';
import PlanCard from '../components/AISubscription/PlanCard';
import LoadingSpinner from '../components/AISubscription/LoadingSpinner';
import { FaCheck } from 'react-icons/fa';

/**
 * Subscription Plans Page
 * Displays available plans and handles subscription purchases
 * Follows Single Responsibility Principle - manages plan selection and payment
 */
const SubscriptionPlans = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { 
    plans, 
    subscription, 
    loading, 
    fetchPlans, 
    createPaymentOrder, 
    verifyAndActivate,
    getTierInfo 
  } = useSubscription();

  const [billingCycle, setBillingCycle] = useState('monthly');
  const [processingPlanId, setProcessingPlanId] = useState(null);

  // Load plans on mount
  useEffect(() => {
    window.scrollTo(0, 0);
    if (plans.length === 0) {
      fetchPlans();
    }
  }, [plans.length, fetchPlans]);

  // Handle plan selection and payment
  const handleSelectPlan = async (plan) => {
    if (!user) {
      toast.info('Please login to subscribe');
      navigate('/login', { state: { from: '/subscription/plans' } });
      return;
    }

    setProcessingPlanId(plan.id);

    try {
      // Create payment order
      const orderResult = await createPaymentOrder({
        plan_id: plan.id,
        billing_cycle: billingCycle,
      });

      if (!orderResult.success) {
        toast.error(orderResult.message || 'Failed to create order');
        setProcessingPlanId(null);
        return;
      }

      const orderData = orderResult.data;

      // Check if Razorpay is loaded
      if (typeof window.Razorpay === 'undefined') {
        toast.error('Payment gateway not loaded. Please refresh the page.');
        setProcessingPlanId(null);
        return;
      }

      // Open Razorpay checkout
      const options = {
        key: orderData.razorpay_key,
        amount: orderData.amount * 100,
        currency: orderData.currency,
        name: 'BrainersHub AI Subscription',
        description: `${plan.name} - ${billingCycle === 'monthly' ? 'Monthly' : 'Annual'}`,
        order_id: orderData.order_id,
        handler: async function (response) {
          // Payment successful, verify on backend
          const verifyResult = await verifyAndActivate({
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
            plan_id: plan.id,
            billing_cycle: billingCycle,
          });

          if (verifyResult.success) {
            toast.success('Subscription activated successfully!');
            navigate('/ai/features');
          } else {
            toast.error(verifyResult.message || 'Payment verification failed');
          }

          setProcessingPlanId(null);
        },
        prefill: {
          name: `${user.firstname} ${user.lastname || ''}`,
          email: user.email,
          contact: user.mobileno || '',
        },
        theme: {
          color: '#7C3AED',
        },
        modal: {
          ondismiss: function() {
            setProcessingPlanId(null);
            toast.info('Payment cancelled');
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();

    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Failed to initiate payment');
      setProcessingPlanId(null);
    }
  };

  if (loading && plans.length === 0) {
    return (
      <div>
        <PublicHeader />
        <div className="min-h-screen flex items-center justify-center">
          <LoadingSpinner size="lg" text="Loading plans..." />
        </div>
        <Footer />
      </div>
    );
  }

  const tierInfo = getTierInfo();
  const currentPlanId = subscription?.plan_id;

  // Mock plans if no plans from backend (for demonstration)
  const displayPlans = plans.length > 0 ? plans : [
    {
      id: 0,
      name: 'Free',
      description: 'Perfect for trying out AI features',
      monthly_price: 0,
      annual_price: 0,
      features: [
        '5 AI quizzes per month',
        '10 doubt solver questions',
        '3 note summaries',
        'Basic study planner',
        'Standard response time',
      ],
    },
    {
      id: 1,
      name: 'Basic',
      description: 'Best for regular learners',
      monthly_price: 199,
      annual_price: 1999,
      features: [
        '50 AI quizzes per month',
        '100 doubt solver questions',
        '25 note summaries',
        '30 flashcard sets',
        'Advanced study planner',
        'Faster responses (2-5s)',
        '30-day history',
        'Email support',
      ],
    },
    {
      id: 2,
      name: 'Premium',
      description: 'For serious exam preparation',
      monthly_price: 499,
      annual_price: 4999,
      features: [
        '✨ Unlimited AI quizzes',
        '✨ Unlimited doubt solving',
        '✨ Unlimited summaries',
        '✨ Unlimited flashcards',
        '10 mock tests per month',
        'Personalized learning path',
        '20 video summaries',
        'Concept explainer',
        'Advanced analytics',
        'Priority processing (<2s)',
        'Unlimited history',
        'Priority support',
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 pb-20 md:pb-8">
      <PublicHeader />
      
      <main className="container mx-auto px-3 sm:px-4 py-6 sm:py-8 md:py-12">
        {/* Header - Mobile Optimized */}
        <div className="text-center mb-8 sm:mb-12">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3 sm:mb-4 px-2">
            Choose Your Plan
          </h1>
          <p className="text-base sm:text-xl text-gray-600 mb-6 sm:mb-8 px-4">
            Unlock AI-powered learning tools to accelerate your success
          </p>

          {/* Billing Cycle Toggle - Mobile Friendly */}
          <div className="inline-flex items-center bg-white rounded-lg shadow-md p-1 mx-auto">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-4 sm:px-6 py-2 sm:py-2.5 rounded-lg font-semibold transition-all duration-200 text-sm sm:text-base touch-manipulation ${
                billingCycle === 'monthly'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`px-4 sm:px-6 py-2 sm:py-2.5 rounded-lg font-semibold transition-all duration-200 flex items-center gap-2 text-sm sm:text-base touch-manipulation ${
                billingCycle === 'annual'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Annual
              <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded whitespace-nowrap">
                Save 17%
              </span>
            </button>
          </div>
        </div>

        {/* Plans Grid - Mobile: Single Column Stack */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto mb-8 sm:mb-12">
          {displayPlans.map((plan, index) => (
            <PlanCard
              key={plan.id}
              plan={{
                ...plan,
                monthly_price: billingCycle === 'annual' 
                  ? Math.round(plan.annual_price / 12) 
                  : plan.monthly_price,
                annual_price: plan.annual_price,
              }}
              isCurrentPlan={plan.id === currentPlanId}
              isRecommended={plan.name === 'Premium'}
              onSelect={plan.id === 0 ? null : handleSelectPlan}
              loading={processingPlanId === plan.id}
            />
          ))}
        </div>

        {/* Feature Comparison Table - Mobile Optimized */}
        <div className="max-w-6xl mx-auto mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-6 sm:mb-8 px-4">
            Feature Comparison
          </h2>
          
          {/* Mobile: Card-based comparison */}
          <div className="lg:hidden space-y-4 px-2">
            {displayPlans.map((plan, index) => (
              <div key={plan.id} className="bg-white rounded-lg shadow-md p-4">
                <h3 className="text-lg font-bold text-gray-900 mb-3 pb-2 border-b">
                  {plan.name} Plan
                </h3>
                <div className="space-y-2 text-sm">
                  {[
                    { feature: 'AI Quiz Generator', value: ['5/month', '50/month', 'Unlimited'][index] },
                    { feature: 'AI Doubt Solver', value: ['10/month', '100/month', 'Unlimited'][index] },
                    { feature: 'Notes Summarizer', value: ['3/month', '25/month', 'Unlimited'][index] },
                    { feature: 'Flashcard Generator', value: ['—', '30/month', 'Unlimited'][index] },
                    { feature: 'Study Planner', value: ['Basic', 'Advanced', 'AI-Powered'][index] },
                    { feature: 'Mock Tests', value: ['—', '—', '10/month'][index] },
                    { feature: 'Response Time', value: ['5-10s', '2-5s', '<2s'][index] },
                  ].map((row, i) => (
                    <div key={i} className="flex justify-between py-1">
                      <span className="text-gray-600">{row.feature}</span>
                      <span className="font-semibold text-gray-900">{row.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Desktop: Table view */}
          <div className="hidden lg:block bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                      Feature
                    </th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">
                      Free
                    </th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">
                      Basic
                    </th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">
                      Premium
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {[
                    { feature: 'AI Quiz Generator', free: '5/month', basic: '50/month', premium: 'Unlimited' },
                    { feature: 'AI Doubt Solver', free: '10/month', basic: '100/month', premium: 'Unlimited' },
                    { feature: 'Notes Summarizer', free: '3/month', basic: '25/month', premium: 'Unlimited' },
                    { feature: 'Flashcard Generator', free: '—', basic: '30/month', premium: 'Unlimited' },
                    { feature: 'Study Planner', free: 'Basic', basic: 'Advanced', premium: 'AI-Powered' },
                    { feature: 'Mock Tests', free: '—', basic: '—', premium: '10/month' },
                    { feature: 'Learning Analytics', free: '—', basic: 'Basic', premium: 'Advanced' },
                    { feature: 'Response Time', free: '5-10s', basic: '2-5s', premium: '<2s' },
                    { feature: 'Priority Support', free: '—', basic: '—', premium: '✓' },
                  ].map((row, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {row.feature}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 text-center">
                        {row.free === '✓' ? <FaCheck className="text-green-500 mx-auto" /> : row.free}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 text-center">
                        {row.basic === '✓' ? <FaCheck className="text-green-500 mx-auto" /> : row.basic}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 text-center">
                        {row.premium === '✓' ? <FaCheck className="text-green-500 mx-auto" /> : row.premium}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* FAQ Section - Mobile Optimized */}
        <div className="max-w-3xl mx-auto px-2">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-6 sm:mb-8">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-3 sm:space-y-4">
            {[
              {
                q: 'Can I cancel my subscription anytime?',
                a: 'Yes! You can cancel your subscription anytime. You\'ll continue to have access until the end of your billing period.',
              },
              {
                q: 'What payment methods do you accept?',
                a: 'We accept all major credit/debit cards, UPI, net banking, and digital wallets through Razorpay.',
              },
              {
                q: 'Can I upgrade or downgrade my plan?',
                a: 'Yes, you can upgrade instantly or schedule a downgrade for your next billing cycle.',
              },
              {
                q: 'Is there a money-back guarantee?',
                a: 'Yes! We offer a 7-day money-back guarantee for first-time subscribers.',
              },
            ].map((faq, index) => (
              <details key={index} className="bg-white rounded-lg shadow-md p-4 sm:p-6 group">
                <summary className="font-semibold text-gray-900 cursor-pointer text-sm sm:text-base list-none flex items-center justify-between touch-manipulation">
                  <span className="pr-4">{faq.q}</span>
                  <span className="text-gray-400 group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <p className="mt-3 text-gray-600 text-sm sm:text-base">
                  {faq.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default SubscriptionPlans;
